import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";

function OrderRow({ item, index, total, onMove, onDrop, testid, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} onDragEnd={onDrop}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`${testid}-row-${item.id}`}>
      <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab touch-none rounded p-1 text-slate-400 hover:bg-slate-100 active:cursor-grabbing"
        aria-label="Drag to move" data-testid={`${testid}-handle-${item.id}`}>
        <GripVertical className="h-5 w-5" />
      </button>
      <span className="w-7 shrink-0 text-center font-mono text-sm text-slate-400">{index + 1}</span>
      <div className="min-w-0 flex-1">{children}</div>
      <div className="flex flex-col">
        <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-25" aria-label="Move up" data-testid={`${testid}-up-${item.id}`}>
          <ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-25" aria-label="Move down" data-testid={`${testid}-down-${item.id}`}>
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>
    </Reorder.Item>
  );
}

export function OrderList({ items, onSave, renderItem, testid, empty = "Nothing here yet." }) {
  const [list, setList] = useState(items);
  const ref = useRef(items);
  useEffect(() => { setList(items); ref.current = items; }, [items]);

  const update = (next) => { ref.current = next; setList(next); };
  const move = (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    update(next);
    onSave(next.map((x) => x.id));
  };

  if (!list.length) return <p className="rounded-xl border border-dashed bg-white p-6 text-center text-sm text-slate-500">{empty}</p>;
  return (
    <Reorder.Group axis="y" values={list} onReorder={update} className="space-y-2" data-testid={`${testid}-list`}>
      {list.map((item, i) => (
        <OrderRow key={item.id} item={item} index={i} total={list.length} onMove={move} onDrop={() => onSave(ref.current.map((x) => x.id))} testid={testid}>
          {renderItem(item, i)}
        </OrderRow>
      ))}
    </Reorder.Group>
  );
}
